import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { startOfYesterday } from 'date-fns';
import { Button, Group, Dialog, Spinner, Table, useToast } from '@economic/taco';
import { refreshCurrentLegacyPage } from '@economic/react-app';
import * as types from '../../../../types/internal-api';
import internalApiClient from '../../../shared/util/api/internalApiClient';
import useQuery from '../../../shared/hooks/useQuery';
import { Form, Field } from '../../../shared/components/Form';
import { Row, Column } from '../../../shared/components/Grid/Grid';

type BlockingCompaniesListProps = { blockingCompanies: types.Company[]; agreementNumber: any; hide: () => void };

function BlockingCompaniesList({ blockingCompanies, agreementNumber, hide }: BlockingCompaniesListProps) {
    return (
        <>
            <Dialog.Title>Deregister administrator {agreementNumber}</Dialog.Title>

            <p>The following agreements are active and paid by this administrator, preventing deactivation</p>
            <Table data={blockingCompanies} className="h-full">
                <Table.Column accessor="agreementNumber" align="left" headRenderer="Agreement" flex="0 1 150px" />
                <Table.Column accessor="name" align="left" headRenderer="Name" flex="0 1 150px" />
                <Table.Column
                    accessor="invoiced"
                    align="left"
                    headRenderer="Invoiced"
                    flex="0 1 100px"
                    cellRenderer={({ value }) => (value ? 'Yes' : 'No')}
                />
            </Table>

            <Dialog.Footer>
                <Group>
                    <Button appearance="primary" onClick={hide}>
                        Close
                    </Button>
                </Group>
            </Dialog.Footer>
        </>
    );
}

type DeregisterAdministratorPanelProps = { agreementNumber: any; hide: () => void };

function DeregisterAdministratorPanel({ agreementNumber, hide }: DeregisterAdministratorPanelProps) {
    const { loading: blockingCompaniesLoading, data: blockingCompanies } = useQuery<types.Company[]>(
        `system/administrator/${agreementNumber}/companies/active/admin-paid`
    );
    const toast = useToast();

    if (blockingCompaniesLoading) {
        return <Spinner />;
    }

    if (blockingCompanies?.length > 0) {
        return <BlockingCompaniesList blockingCompanies={blockingCompanies} agreementNumber={agreementNumber} hide={hide} />;
    }

    async function handleSubmit(values) {
        const activeToast = toast.loading('Loading');

        try {
            if (values.date < startOfYesterday()) {
                activeToast.error('Date cannot be before yesterday');
                return;
            }

            await internalApiClient.post(`system/administrator/${agreementNumber}/deregister`, values.date);

            activeToast.success('Deregistration date has been set for administrator');
            refreshCurrentLegacyPage('content');
            hide();
        } catch {
            activeToast.error('Error occured while setting deregisteration date for administrator');
        }
    }

    return (
        <Form onSubmit={handleSubmit} skipNotifications>
            <Dialog.Title>Deregistrate administrator {agreementNumber}</Dialog.Title>

            <Row>
                <Column xs="6">
                    <Field.DatePicker label="Deregistration date" required name="date" />
                </Column>
            </Row>

            <Dialog.Footer>
                <Group>
                    <Button appearance="default" onClick={hide}>
                        Cancel
                    </Button>
                    <Button appearance="primary" autoFocus type="submit">
                        Ok
                    </Button>
                </Group>
            </Dialog.Footer>
        </Form>
    );
}

export default function DeregisterAdministratorDialog() {
    const navigate = useNavigate();
    const location = useLocation();
    let { agreementNumber } = location.state || ({} as any);

    function hide() {
        navigate(location.pathname);
    }

    return (
        <Dialog open={location.hash === '#deregister-administrator'} size="xs" onClose={hide}>
            <Dialog.Content aria-label="">
                {({ close }) => (
                    <DeregisterAdministratorPanel
                        agreementNumber={agreementNumber}
                        hide={() => {
                            close();
                            hide();
                        }}
                    />
                )}
            </Dialog.Content>
        </Dialog>
    );
}
